import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useWallet } from '@/context/WalletContext';
import { Button } from '@/components/ui/button';
import {
  User, Mail, Shield, Wallet, Bell, Trash2, Check, AlertTriangle,
  Calendar, Lock, Eye, EyeOff, Save, Coins, Vote, TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Section({ title, icon: Icon, children, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 px-6 py-4 border-b border-white/5">
        <Icon className="w-5 h-5 text-blue-400" />
        <h2 className="font-semibold">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </motion.div>
  );
}

export default function Settings() {
  const { user, token, logout } = useAuth();
  const { address, isConnected, connectWallet, disconnectWallet } = useWallet();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Profile
  const [name, setName] = useState(user?.name || '');
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  // Password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  // Email Prefs
  const [emailPrefs, setEmailPrefs] = useState({ email_notifications: true, email_types: {}, email_enabled: false });
  const [prefsSaved, setPrefsSaved] = useState(false);

  // Delete
  const [showDelete, setShowDelete] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/notifications/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setEmailPrefs)
      .catch(() => {});
  }, [token]);

  const saveProfile = async () => {
    setProfileLoading(true);
    try {
      await fetch(`${API}/api/auth/profile`, { method: 'PUT', headers, body: JSON.stringify({ name }) });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch {} finally { setProfileLoading(false); }
  };

  const changePassword = async () => {
    setPwMsg(null);
    if (newPw !== confirmPw) { setPwMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    if (newPw.length < 6) { setPwMsg({ type: 'error', text: 'Password must be at least 6 characters' }); return; }
    setPwLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'PUT', headers, body: JSON.stringify({ current_password: currentPw, new_password: newPw })
      });
      const data = await res.json();
      if (res.ok) {
        setPwMsg({ type: 'success', text: 'Password changed successfully' });
        setCurrentPw(''); setNewPw(''); setConfirmPw('');
      } else {
        setPwMsg({ type: 'error', text: data.detail || 'Failed to change password' });
      }
    } catch { setPwMsg({ type: 'error', text: 'Network error' }); }
    finally { setPwLoading(false); }
  };

  const toggleEmail = async () => {
    const newVal = !emailPrefs.email_notifications;
    setEmailPrefs(prev => ({ ...prev, email_notifications: newVal }));
    await fetch(`${API}/api/notifications/preferences`, {
      method: 'PUT', headers, body: JSON.stringify({ email_notifications: newVal })
    });
    setPrefsSaved(true);
    setTimeout(() => setPrefsSaved(false), 1500);
  };

  const toggleType = async (key) => {
    const updated = { ...emailPrefs.email_types, [key]: !emailPrefs.email_types[key] };
    setEmailPrefs(prev => ({ ...prev, email_types: updated }));
    await fetch(`${API}/api/notifications/preferences`, {
      method: 'PUT', headers, body: JSON.stringify({ email_types: updated })
    });
  };

  const deleteAccount = async () => {
    setDeleteError('');
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/account`, {
        method: 'DELETE', headers, body: JSON.stringify({ password: deletePw })
      });
      if (res.ok) {
        logout();
        navigate('/');
      } else {
        const data = await res.json();
        setDeleteError(data.detail || 'Failed to delete account');
      }
    } catch { setDeleteError('Network error'); }
    finally { setDeleteLoading(false); }
  };

  const notifCategories = [
    { key: 'loan_request', label: 'New loan requests', icon: Coins, desc: 'When borrowers post new loan requests' },
    { key: 'bid_received', label: 'Bids on my loans', icon: TrendingUp, desc: 'When lenders bid on your loan requests' },
    { key: 'bid_accepted', label: 'Bid accepted / rejected', icon: Check, desc: 'When your bid is accepted or another is chosen' },
    { key: 'governance', label: 'Governance proposals', icon: Vote, desc: 'New proposals and voting updates' },
  ];

  return (
    <DashboardLayout title="Settings">
      <div className="max-w-3xl mx-auto space-y-6 pb-12" data-testid="settings-page">

        {/* Profile */}
        <Section title="Profile" icon={User} testId="profile-section">
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
                data-testid="profile-name-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Email</label>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.02] border border-white/5 rounded-xl text-gray-400">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-sm" data-testid="profile-email">{user?.email}</span>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Role</label>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.02] border border-white/5 rounded-xl text-gray-400">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span className="text-sm capitalize" data-testid="profile-role">{user?.role}</span>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Member Since</label>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span data-testid="profile-date">{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '--'}</span>
              </div>
            </div>
            <div className="pt-2">
              <Button
                onClick={saveProfile}
                disabled={profileLoading || name === user?.name}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:brightness-110"
                data-testid="save-profile-btn"
              >
                {profileSaved ? <><Check className="w-4 h-4 mr-1.5" /> Saved</> : <><Save className="w-4 h-4 mr-1.5" /> Save Changes</>}
              </Button>
            </div>
          </div>
        </Section>

        {/* Wallet */}
        <Section title="Wallet" icon={Wallet} testId="wallet-section">
          <div className="space-y-4">
            {isConnected ? (
              <>
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">Connected Address</label>
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-green-500/5 border border-green-500/20 rounded-xl">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <code className="text-sm text-green-400 font-mono flex-1" data-testid="wallet-address">{address}</code>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-400">
                  <span>Network: <span className="text-blue-400">Base (8453)</span></span>
                </div>
                <Button variant="outline" onClick={disconnectWallet} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="settings-disconnect-wallet-btn">
                  Disconnect Wallet
                </Button>
              </>
            ) : (
              <div className="text-center py-4">
                <Wallet className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-400 mb-4">Connect your wallet to interact with smart contracts. Supports MetaMask, WalletConnect, and more.</p>
                <Button onClick={connectWallet} className="bg-gradient-to-r from-blue-500 to-purple-500" data-testid="settings-connect-wallet-btn">
                  <Wallet className="w-4 h-4 mr-2" /> Connect Wallet
                </Button>
              </div>
            )}
          </div>
        </Section>

        {/* Notifications */}
        <Section title="Notification Preferences" icon={Bell} testId="notifications-section">
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Email Notifications</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {emailPrefs.email_enabled ? 'Receive email alerts for platform activity' : 'Email service not configured — preferences saved for when it goes live'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {prefsSaved && <span className="text-xs text-green-400">Saved</span>}
                <button
                  onClick={toggleEmail}
                  className={`relative w-11 h-6 rounded-full transition-colors ${emailPrefs.email_notifications ? 'bg-blue-500' : 'bg-gray-700'}`}
                  data-testid="email-master-toggle"
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${emailPrefs.email_notifications ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            <div className="border-t border-white/5 pt-4 space-y-3">
              <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">Categories</div>
              {notifCategories.map(cat => (
                <div key={cat.key} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <cat.icon className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-sm">{cat.label}</div>
                      <div className="text-xs text-gray-600">{cat.desc}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleType(cat.key)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${emailPrefs.email_types?.[cat.key] !== false ? 'bg-blue-500/80' : 'bg-gray-700'}`}
                    data-testid={`toggle-${cat.key}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${emailPrefs.email_types?.[cat.key] !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Security */}
        <Section title="Security" icon={Lock} testId="security-section">
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPw}
                  onChange={e => setCurrentPw(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500 pr-10"
                  data-testid="current-password-input"
                />
                <button onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">New Password</label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPw}
                    onChange={e => setNewPw(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500 pr-10"
                    data-testid="new-password-input"
                  />
                  <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={e => setConfirmPw(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            {pwMsg && (
              <div className={`p-3 rounded-lg text-sm ${pwMsg.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`} data-testid="password-message">
                {pwMsg.text}
              </div>
            )}
            <Button
              onClick={changePassword}
              disabled={pwLoading || !currentPw || !newPw || !confirmPw}
              variant="outline"
              className="border-white/10"
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-1.5" /> Change Password
            </Button>
          </div>
        </Section>

        {/* Danger Zone */}
        <Section title="Account" icon={AlertTriangle} testId="account-section">
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Deleting your account is permanent. All your data, loan requests, bids, and notifications will be removed.
            </p>
            {!showDelete ? (
              <Button
                variant="outline"
                onClick={() => setShowDelete(true)}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid="show-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete Account
              </Button>
            ) : (
              <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20 space-y-3">
                <p className="text-sm text-red-400 font-medium">Are you sure? Enter your password to confirm.</p>
                <input
                  type="password"
                  value={deletePw}
                  onChange={e => setDeletePw(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-4 py-2.5 bg-white/5 border border-red-500/20 rounded-xl focus:outline-none focus:border-red-500"
                  data-testid="delete-password-input"
                />
                {deleteError && <p className="text-xs text-red-400">{deleteError}</p>}
                <div className="flex gap-3">
                  <Button
                    onClick={deleteAccount}
                    disabled={deleteLoading || !deletePw}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowDelete(false); setDeletePw(''); setDeleteError(''); }} data-testid="cancel-delete-btn">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Section>
      </div>
    </DashboardLayout>
  );
}
